import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import { gsap } from "gsap";
import { useTheme } from "../../ThemeContext";
import "./styles/navBar.css";

const LINKS = [
	{ to: "/",              label: "Home",           key: "home" },
	{ to: "/projects",      label: "Projects",       key: "projects" },
	{ to: "/certifications",label: "Certifications", key: "certifications" },
	{ to: "/about",         label: "About",          key: "about" },
	{ to: "/contact",       label: "Contact",        key: "contact" },
];

const NavBar = ({ active }) => {
	const [scrolled, setScrolled] = useState(false);
	const { dark, toggle } = useTheme();
	const navRef   = useRef(null);
	const pillRef  = useRef(null);
	const itemRefs = useRef([]);

	// ── Scroll detection ──
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 20);
		window.addEventListener("scroll", onScroll);
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// ── GSAP entrance ──
	useEffect(() => {
		if (!navRef.current) return;
		gsap.fromTo(navRef.current,
			{ y: -60, opacity: 0, scale: 0.95 },
			{ y: 0,   opacity: 1, scale: 1, duration: 0.8, ease: "power3.out", delay: 0.2 }
		);
		gsap.fromTo(itemRefs.current,
			{ y: -10, opacity: 0 },
			{ y: 0,   opacity: 1, duration: 0.5, ease: "power2.out", stagger: 0.07, delay: 0.5 }
		);
	}, []);

	// ── Slide active pill ──
	useEffect(() => {
		const activeIdx = LINKS.findIndex(l => l.key === active);
		const activeEl  = itemRefs.current[activeIdx];
		const pill      = pillRef.current;
		if (!activeEl || !pill) return;

		const { offsetLeft, offsetWidth } = activeEl;
		gsap.to(pill, {
			x: offsetLeft,
			width: offsetWidth,
			duration: 0.4,
			ease: "power2.inOut",
		});
	}, [active]);

	// ── Item hover ──
	const onEnter = (i) => {
		if (LINKS[i].key === active) return;
		gsap.to(itemRefs.current[i], { y: -2, duration: 0.2, ease: "power2.out" });
	};
	const onLeave = (i) => {
		gsap.to(itemRefs.current[i], { y: 0, duration: 0.2, ease: "power2.out" });
	};

	return (
		<div className="nav-container">
			<nav ref={navRef} className={`navbar ${scrolled ? "navbar-scrolled" : ""}`}>

				{/* Logo */}
				<Link to="/" className="nav-logo">
					<span className="nav-logo-dot" />
					<span className="nav-logo-text">Hamdan</span>
				</Link>

				<div className="nav-divider" />

				{/* Links */}
				<div className="nav-links-wrap">
					{/* sliding pill */}
					<span ref={pillRef} className="nav-pill" />

					<ul className="nav-list">
						{LINKS.map(({ to, label, key }, i) => (
							<li
								key={key}
								ref={el => itemRefs.current[i] = el}
								className={`nav-item ${active === key ? "active" : ""}`}
								onMouseEnter={() => onEnter(i)}
								onMouseLeave={() => onLeave(i)}
							>
								<Link to={to}>{label}</Link>
							</li>
						))}
					</ul>
				</div>

				<div className="nav-divider" />

				{/* Theme toggle */}
				<button className="nav-theme-toggle" onClick={toggle} aria-label="Toggle theme">
					<FontAwesomeIcon icon={dark ? faSun : faMoon} />
				</button>

			</nav>
		</div>
	);
};

export default NavBar;
